import {
  TEXT_AREA_IDS,
  ANALYZING_TEXT,
  TEXT_AREA_PLACEHOLDER,
  EDITING_BUTTON_TOOLTIP,
  EDITING_BUTTON_TOOLTIP_EMPTY,
  AI_RESPONSE_ERRORS,
} from 'components/AiMode/constants';
import cx from 'classnames';
import { memo, useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import { getAiChatContent } from 'utils/aiChat';
import { useChatInput } from 'hooks/useChatInput';
import { useAppContext } from 'context/AppContext';
import { MessageRoleType, MessageType } from 'types';
import { Avatar } from 'packages/radiant-react/avatar';
import { useStyleContext } from 'context/StyleContext';
import { Button } from 'packages/radiant-react/button';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { TextArea } from 'packages/radiant-react/text-area';
import { Horizontal, Vertical } from 'packages/radiant-react/layout';
import { Colors, Typography } from 'packages/radiant-react/typography';
import { LoadingIndicator } from 'packages/radiant-react/loading-indicator';
import { Icon, IconColor, IconID, IconSize } from 'packages/radiant-react/icon';

function AvatarAndEditButton({
  message,
  isEditing,
  setIsEditing,
  isLastUserMessage,
  userName,
}: {
  message: MessageType;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  isLastUserMessage: boolean;
  userName: string;
}) {
  const { isStreaming } = useAppContext();
  const isShowEditButton = isLastUserMessage && !isEditing && !isStreaming;

  return (
    <Vertical className={styles['container-logo']}>
      {message.role === MessageRoleType.USER ? (
        <Horizontal className={styles['user-first-char-container']}>
          <Avatar className={styles.userAvatar} size="s" name={userName} />
          {isShowEditButton && (
            <Button
              type="secondary"
              size="s"
              icon={IconID.PENCIL}
              onClick={() => setIsEditing(true)}
            />
          )}
        </Horizontal>
      ) : (
        <Typography variant="caption" className={styles['ai-icon']}>
          <Icon id={IconID.SPOTTER_CODE} size={IconSize.XLARGE} />
        </Typography>
      )}
    </Vertical>
  );
}

function Loader() {
  return (
    <Horizontal className={styles['loading-indicator']}>
      <LoadingIndicator.Contextual className={styles['loading-dot']} />
      <Typography variant="body-normal" color={Colors.accent} className={styles['loading-text']}>
        {ANALYZING_TEXT}
      </Typography>
    </Horizontal>
  );
}

function EditViewContainer({
  displayContent,
  handleTextareaInput,
  handleEnterKeyPress,
  setIsEditing,
  handleArrowUpClick,
  userInputText,
}: {
  displayContent: string;
  handleTextareaInput: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleEnterKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  setIsEditing: (isEditing: boolean) => void;
  handleArrowUpClick: () => void;
  userInputText: string;
}) {
  const { updateLastTwoMessages } = useAppContext();
  const isButtonDisabled = userInputText === displayContent || !userInputText.trim();
  const displayButtonTooltip = !userInputText.trim()
    ? EDITING_BUTTON_TOOLTIP_EMPTY
    : EDITING_BUTTON_TOOLTIP;

  return (
    <Vertical className={styles['editing-container']}>
      <TextArea
        id={TEXT_AREA_IDS.EDITING}
        value={userInputText}
        placeholder={TEXT_AREA_PLACEHOLDER}
        autoFocus
        className={styles['chat-input-textarea']}
        onChange={handleTextareaInput}
        onFocus={(e) => {
          const el = e.currentTarget;
          el.setSelectionRange(el.value.length, el.value.length);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            updateLastTwoMessages(userInputText);
            setIsEditing(false);
            handleEnterKeyPress(e);
          }
        }}
      />
      <Horizontal hAlignContent="right" className={styles['editing-buttons']}>
        <Button type="secondary" text="Cancel" onClick={() => setIsEditing(false)} />
        <Button
          type="primary"
          text="Done"
          onClick={() => {
            updateLastTwoMessages(userInputText);
            setIsEditing(false);
            handleArrowUpClick();
          }}
          isDisabled={isButtonDisabled}
          tooltip={displayButtonTooltip}
        />
      </Horizontal>
    </Vertical>
  );
}

function AiResponseIssue({ displayContent }: { displayContent: string }) {
  const errorheading = AI_RESPONSE_ERRORS[displayContent]?.heading || 'Error occurred!';
  const errorpara = AI_RESPONSE_ERRORS[displayContent]?.para || '';

  return (
    <Vertical className={`${styles['container-content']}`}>
      <Horizontal vAlignContent="center">
        <Icon
          id={IconID.EXCLAMATION_POINT_CIRCLE}
          size={IconSize.MEDIUM}
          className={styles['error-message-icon']}
          color={IconColor.RED}
        />
        <Typography variant="body-normal" color={Colors.failure}>
          {errorheading}
        </Typography>
      </Horizontal>
      <Typography variant="body-normal">{errorpara}</Typography>
    </Vertical>
  );
}

function ManualModeButton({ message }: { message: MessageType }) {
  const { setIsAiModeActive } = useAppContext();
  const isButtonShown = !(message.role === MessageRoleType.USER) && !!message.isManualModeActive;

  if (!isButtonShown) {
    return null;
  }

  return (
    <Vertical className={styles['out-of-scope-prompts']}>
      <Button
        type="secondary"
        size="m"
        text="Go to Manual mode"
        className={styles['out-of-scope-prompts-button']}
        onClick={() => {
          setIsAiModeActive(false);
        }}
      />
    </Vertical>
  );
}

function ChatItemContentView({
  isLoading,
  displayContent,
  isExpanded,
  isAiResponseIssue,
  handleTextareaInput,
  handleEnterKeyPress,
  setIsEditing,
  handleArrowUpClick,
  userInputText,
  isEditing,
  message,
  toggleExpand,
  hasMoreFiles,
}: {
  isLoading: boolean;
  displayContent: string;
  isExpanded: boolean;
  isAiResponseIssue: boolean;
  handleTextareaInput: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleEnterKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  setIsEditing: (isEditing: boolean) => void;
  handleArrowUpClick: () => void;
  userInputText: string;
  isEditing: boolean;
  message: MessageType;
  toggleExpand: () => void;
  hasMoreFiles: boolean;
}) {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const userContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userContentRef.current) return;
    const element = userContentRef.current;
    const isTextOverflowing = element.scrollHeight > element.clientHeight;
    setIsOverflowing(isTextOverflowing);
  }, [displayContent]);

  if (isLoading) {
    return <Loader />;
  }

  if (isEditing) {
    return (
      <EditViewContainer
        displayContent={displayContent}
        handleTextareaInput={handleTextareaInput}
        handleEnterKeyPress={handleEnterKeyPress}
        setIsEditing={setIsEditing}
        handleArrowUpClick={handleArrowUpClick}
        userInputText={userInputText}
      />
    );
  }

  if (isAiResponseIssue) {
    return <AiResponseIssue displayContent={displayContent} />;
  }

  return (
    <Vertical className={`${styles['container-content']}`}>
      {message.role === MessageRoleType.USER ? (
        <Vertical className={styles.userMessageContainer}>
          <div
            ref={userContentRef}
            className={cx({
              [styles.userMessageContent]: !isExpanded,
            })}
          >
            <Typography variant="body-normal">{displayContent}</Typography>
          </div>
          {(isOverflowing || hasMoreFiles) && (
            <Button
              type="secondary"
              size="s"
              icon={IconID.CHEVRON_DOWN}
              className={cx(styles.expandButton, {
                [styles.expanded]: isExpanded,
              })}
              onClick={toggleExpand}
            />
          )}
        </Vertical>
      ) : (
        <MarkdownRenderer content={displayContent} />
      )}
      <ManualModeButton message={message} />
    </Vertical>
  );
}

function FilePreview({
  message,
  isExpanded,
  hasMoreFiles,
  remainingFilesCount,
}: {
  message: MessageType;
  isExpanded: boolean;
  hasMoreFiles: boolean;
  remainingFilesCount: number;
}) {
  return (
    <Horizontal className={styles['container-preview-container']} vAlignContent="center">
      <Vertical className={styles['container-preview']}>
        {message?.images?.slice(0, isExpanded ? undefined : 1).map((img) => (
          <Horizontal className={styles['preview-item']} key={img.id} vAlignContent="center">
            <img src={img.content} alt="image" className={styles['preview-item-image']} />
            <Vertical className={styles['preview-item-content']} vAlignContent="center">
              <Typography variant="p" className={styles['preview-item-name']}>
                {img.name}
              </Typography>
              <Typography variant="body-normal" className={styles['preview-item-size']}>
                {img.size}
              </Typography>
            </Vertical>
          </Horizontal>
        ))}

        {(!message?.images?.length || isExpanded) &&
          message?.pdfFiles?.slice(0, isExpanded ? undefined : 1).map((pdf) => (
            <Horizontal className={styles['preview-item']} key={pdf.id} vAlignContent="center">
              <Icon
                id={IconID.DOC_SEARCH_PDF}
                size={IconSize.MEDIUM}
                className={styles['preview-item-image']}
              />
              <Vertical className={styles['preview-item-content']} vAlignContent="center">
                <Typography variant="p" className={styles['preview-item-name']}>
                  {pdf.name}
                </Typography>
                <Typography variant="body-normal" className={styles['preview-item-size']}>
                  {pdf.size}
                </Typography>
              </Vertical>
            </Horizontal>
          ))}
      </Vertical>
      {!isExpanded && hasMoreFiles && (
        <Typography variant="p-bold" className={styles['remaining-count']}>
          +{remainingFilesCount}
        </Typography>
      )}
    </Horizontal>
  );
}

function ChatItemInner({
  message,
  isLastUserMessage,
  isLastAiMessage,
  itemRef,
}: {
  message: MessageType;
  isLastUserMessage: boolean;
  isLastAiMessage: boolean;
  itemRef?: React.Ref<HTMLDivElement>;
}) {
  const [isEditing, setIsEditing] = useState(false);

  const { styleStateKey } = useStyleContext();
  const {
    handleTextareaInput,
    handleEnterKeyPress,
    handleArrowUpClick,
    userInput,
    updateUserInput,
  } = useChatInput();
  const { userName, totalFiles, hasMoreFiles, remainingFilesCount, isAiResponseIssue } =
    getAiChatContent({ styleStateKey, message });
  const [isExpanded, setIsExpanded] = useState(false);
  const isLoading = message.role === MessageRoleType.AI && message.content === '';

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const updateIsEditing = () => {
    setIsEditing(true);
    updateUserInput(message.content);
  };

  return (
    <Vertical
      className={`${styles['main-container']} ${
        message.role === MessageRoleType.USER ? styles['user-message'] : ''
      } ${isEditing ? styles['editing-main-container'] : ''} ${
        isLastAiMessage ? 'global-last-ai-response' : ''
      }`}
      ref={itemRef}
    >
      <Vertical className={styles['container']}>
        <AvatarAndEditButton
          message={message}
          isEditing={isEditing}
          setIsEditing={updateIsEditing}
          isLastUserMessage={isLastUserMessage}
          userName={userName}
        />

        <ChatItemContentView
          isLoading={isLoading}
          displayContent={message.content}
          isExpanded={isExpanded}
          isAiResponseIssue={isAiResponseIssue}
          handleTextareaInput={handleTextareaInput}
          handleEnterKeyPress={handleEnterKeyPress}
          setIsEditing={setIsEditing}
          handleArrowUpClick={handleArrowUpClick}
          userInputText={userInput}
          isEditing={isEditing}
          message={message}
          toggleExpand={toggleExpand}
          hasMoreFiles={hasMoreFiles}
        />

        {!isEditing && totalFiles > 0 && (
          <FilePreview
            message={message}
            isExpanded={isExpanded}
            hasMoreFiles={hasMoreFiles}
            remainingFilesCount={remainingFilesCount}
          />
        )}
      </Vertical>
    </Vertical>
  );
}

const ChatItem = memo(ChatItemInner);
export default ChatItem;
